import type { Vehicle, Dealer, Customer, Proposal, Address, Employment } from "@cf247/pricing/dist/types";
import {
  DealerType,
  DrivingLicence,
  EmploymentStatus,
  FinanceType,
  ResidentialStatus,
  VehicleType,
  WheelBase,
  AncillaryProductLenderStanceType,
} from "@cf247/pricing/dist/enums";
import type { VehicleDetailModel } from "@/models/vehicle";
import type { SelectedDealer } from "@/types/dealer";
import type {
  PrepareApplicationAddress,
  PrepareApplicationCustomer,
  PrepareApplicationEmployment,
  PrepareApplicationProposal,
} from "@/models/underwriting";

/**
 * Client-side ports of the workscreens plugin's pricing mappers
 * (plugin-customer-journey/src/utility/mappers.ts). They turn the raw
 * prepare-application / vehicle-search data into the shapes `calculate()` expects.
 */

function buildStreet(a: PrepareApplicationAddress): string {
  return [a.buildingNumber, a.buildingName, a.streetName, a.subBuilding].filter(Boolean).join(" ");
}

function mapAddress(a: PrepareApplicationAddress, index: number): Address {
  return {
    index,
    street: buildStreet(a),
    town: a.town,
    postcode: a.postcode,
    status: a.addressStatus.addressStatusId as ResidentialStatus,
    yearsAt: a.addressStatus.yearsAtAddress,
    monthsAt: a.addressStatus.monthsAtAddress,
  };
}

function mapEmployment(e: PrepareApplicationEmployment, index: number): Employment {
  return {
    index,
    employer: e.employerName ?? "",
    occupation: e.occupation,
    status: e.occupationStatus.occupationStatusId as EmploymentStatus,
    yearsAt: e.yearsAtEmployment,
    monthsAt: e.monthsAtEmployment,
  };
}

export function mapCustomer(customer: PrepareApplicationCustomer): Customer {
  return {
    firstName: customer.firstName ?? "",
    surname: customer.lastName ?? "",
    dateOfBirth: customer.dateOfBirth ? new Date(customer.dateOfBirth) : undefined,
    drivingLicence:
      (customer.drivingLicenceType?.drivingLicenceTypeId as DrivingLicence) ?? DrivingLicence.None,
    netMonthlyIncome: customer.bankDetails?.netMonthlyIncome ?? undefined,
    affiliateUserId: customer.affiliateUserId ?? undefined,
    creditCheckScore: customer.creditCheckScore ?? undefined,
    addressHistory: (customer.addresses ?? []).map(mapAddress),
    employmentHistory: (customer.employmentHistory ?? []).map(mapEmployment),
  };
}

export function mapProposal(proposal: PrepareApplicationProposal): Proposal {
  return {
    ...proposal,
    financeType: proposal.financeType ?? FinanceType.HP,
    modifiedOn: proposal.modifiedOn ? new Date(proposal.modifiedOn) : new Date(),
  } as Proposal;
}

export function mapProposals(proposals: PrepareApplicationProposal[] | null | undefined): Proposal[] {
  return (proposals ?? []).map(mapProposal);
}

export interface AdminFee {
  amount: number;
  includedInFinance: boolean;
}

/** Agent/quote admin fee wins; without a quote, fall back to the dealer's admin fee in finance. */
export function resolveAdminFee(
  overrideAdminFee: AdminFee | null,
  hasQuote: boolean,
  dealer: SelectedDealer | null
): AdminFee | null {
  if (overrideAdminFee != null) return overrideAdminFee;
  if (hasQuote || dealer?.adminFee == null) return null;
  return { amount: dealer.adminFee, includedInFinance: true };
}

export function mapDealer(
  dealer: SelectedDealer | null,
  adminFee?: { amount: number; includedInFinance: boolean } | null
): Dealer {
  if (!dealer) {
    return { type: DealerType.Unknown, adminFee: adminFee ?? null, reviews: [] };
  }
  return {
    id: dealer.dealerId,
    type: DealerType.Unknown,
    name: dealer.dealerName,
    town: dealer.town,
    postcode: dealer.postcode,
    reviews: [],
    adminFee: adminFee ?? null,
  };
}

export function mapVehicle(vehicle: VehicleDetailModel, dealer: Dealer): Vehicle {
  return {
    vehicleId: vehicle.vehicleId,
    vrm: vehicle.regNumber,
    year: vehicle.year ?? undefined,
    registrationDate: vehicle.registrationDate ? new Date(vehicle.registrationDate) : null,
    vehicleType: (vehicle.vehicleType as VehicleType) ?? VehicleType.Car,
    make: vehicle.make,
    model: vehicle.model,
    trim: vehicle.trim,
    derivative: vehicle.trim ?? "",
    colour: vehicle.colour,
    mileage: vehicle.mileage ?? undefined,
    wheelBaseType: WheelBase.SHORT,
    insuranceGroup: "",
    taxPerYear: 0,
    previousOwners: 0,
    isWrittenOff: false,
    price: vehicle.price,
    otrPrice: vehicle.price,
    dealer,
  };
}

export function mapSnapshotVehicle(vehicle: Vehicle, dealer: Dealer): Vehicle {
  return {
    vehicleId: vehicle.vehicleId,
    vrm: vehicle.vrm,
    year: vehicle.year,
    registrationDate: vehicle.registrationDate,
    vehicleType: vehicle.vehicleType,
    make: vehicle.make,
    model: vehicle.model,
    derivative: vehicle.derivative ?? vehicle.trim ?? "",
    colour: vehicle.colour,
    mileage: vehicle.mileage,
    wheelBaseType: vehicle.wheelBaseType,
    insuranceGroup: vehicle.insuranceGroup ?? "",
    taxPerYear: vehicle.taxPerYear ?? 0,
    previousOwners: vehicle.previousOwners ?? 0,
    isWrittenOff: vehicle.isWrittenOff ?? false,
    price: vehicle.price,
    otrPrice: vehicle.otrPrice,
    dealer: mapSnapshotDealer(dealer),
  };
}

export function mapSnapshotDealer(dealer: Dealer): Dealer {
  return {
    id: dealer.id,
    type: DealerType.Unknown,
    name: dealer.name,
    postcode: dealer.postcode,
    reviews: dealer.reviews ?? [],
    adminFee: dealer.adminFee ?? null,
  };
}

export function mapSnapshotCustomer(customer: Customer): Customer {
  return {
    firstName: customer.firstName,
    surname: customer.surname,
    dateOfBirth: customer.dateOfBirth
      ? new Date(customer.dateOfBirth)
      : undefined,
    drivingLicence: customer.drivingLicence ?? DrivingLicence.None,
    netMonthlyIncome: customer.netMonthlyIncome,
    affiliateUserId: customer.affiliateUserId,
    creditCheckScore: customer.creditCheckScore,
    addressHistory: customer.addressHistory ?? [],
    employmentHistory: customer.employmentHistory ?? [],
  };
}

/** Maps chosen extras onto the calculate() `includeExtras` argument. */
export function mapExtras(
  extras: { vehicleExtraOptionId: number; paymentMethod: "cash" | "finance" }[] | null | undefined
): { productId: number; stance: AncillaryProductLenderStanceType }[] | null {
  if (!extras) return null;
  return extras.map((extra) => ({
    productId: extra.vehicleExtraOptionId,
    stance:
      extra.paymentMethod === "cash"
        ? AncillaryProductLenderStanceType.Cash
        : AncillaryProductLenderStanceType.Finance,
  }));
}
